import React, { useState, useEffect } from 'react';

export default function PayTableModal({ isOpen, onClose, paytable, onSave, paytableNames, activePaytable, onPaytableClick, isNewPaytable, defaultPaytables, isPremium = true }) {
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedPaytable, setEditedPaytable] = useState({});
  const [editedName, setEditedName] = useState('');
  const [showOverwriteConfirm, setShowOverwriteConfirm] = useState(false);
  const [showRestoreConfirm, setShowRestoreConfirm] = useState(false);

  useEffect(() => {
    if (isOpen && paytable) {
      setEditedPaytable(JSON.parse(JSON.stringify(paytable)));
      setEditedName(isNewPaytable ? '' : activePaytable);
      setIsEditMode(isNewPaytable);
      setShowOverwriteConfirm(false);
    }
  }, [isOpen, paytable, activePaytable, isNewPaytable]);

  if (!isOpen) {
    return null;
  }

  const handleSave = () => {
    const trimmedName = editedName.trim();
    
    // Check for empty name
    if (!trimmedName) {
      alert('Please enter a name for the pay table.');
      return;
    }
    
    // Check if name exists (and it's not the current paytable being edited)
    const nameExists = paytableNames.includes(trimmedName) && (isNewPaytable || trimmedName !== activePaytable);
    
    if (nameExists) {
      setShowOverwriteConfirm(true);
      return;
    }
    
    onSave(editedPaytable, trimmedName);
    setIsEditMode(false);
  };

  const handleOverwriteConfirm = () => {
    onSave(editedPaytable, editedName.trim());
    setShowOverwriteConfirm(false);
    setIsEditMode(false);
  };

  const handleOverwriteCancel = () => {
    setShowOverwriteConfirm(false);
  };

  const handleRestoreDefault = () => {
    setShowRestoreConfirm(true);
  };

  const handleRestoreConfirm = () => {
    // Check if this paytable exists in defaults
    if (defaultPaytables && defaultPaytables[activePaytable]) {
      const defaultPaytable = JSON.parse(JSON.stringify(defaultPaytables[activePaytable]));
      setEditedPaytable(defaultPaytable);
      onSave(defaultPaytable, activePaytable);
      setShowRestoreConfirm(false);
      setIsEditMode(false);
    } else {
      alert('No default paytable found for ' + activePaytable);
      setShowRestoreConfirm(false);
    }
  };

  const handleRestoreCancel = () => {
    setShowRestoreConfirm(false);
  };

  const handleCancel = () => {
    setEditedPaytable(JSON.parse(JSON.stringify(paytable)));
    setEditedName(activePaytable);
    setIsEditMode(false);
    setShowOverwriteConfirm(false);
    if (isNewPaytable) {
      onClose();
    }
  };

  const handleCellChange = (spots, hitNum, value) => {
    const numValue = parseInt(value) || 0;
    setEditedPaytable(prev => ({
      ...prev,
      [spots]: {
        ...prev[spots],
        [hitNum]: numValue
      }
    }));
  };

  // Calculate binomial coefficient C(n, k)
  const combination = (n, k) => {
    if (k > n) return 0;
    if (k === 0 || k === n) return 1;
    
    let result = 1;
    for (let i = 0; i < k; i++) {
      result *= (n - i);
      result /= (i + 1);
    }
    return result;
  };

  // Calculate theoretical RTP for a spot count
  const calculateRTP = (spots) => {
    const n = parseInt(spots);
    const totalCombinations = combination(80, 20);
    let expectedValue = 0;

    for (let hits = 0; hits <= n; hits++) {
      const probability = (combination(n, hits) * combination(80 - n, 20 - hits)) / totalCombinations;
      const payout = currentPaytable[spots][hits] || 0;
      expectedValue += probability * payout;
    }

    return (expectedValue * 100).toFixed(2);
  };

  const currentPaytable = isEditMode ? editedPaytable : paytable;
  const spotCounts = Object.keys(currentPaytable)
    .filter(spots => parseInt(spots) >= 2) // Only show 2-10 spots
    .sort((a, b) => parseInt(a) - parseInt(b));

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000
    }}>
      {showOverwriteConfirm && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          backgroundColor: 'white',
          padding: '24px',
          borderRadius: '8px',
          boxShadow: '0 8px 16px rgba(0, 0, 0, 0.3)',
          zIndex: 1001,
          minWidth: '300px'
        }}>
          <h3 style={{ marginTop: 0 }}>Name Already Exists</h3>
          <p>A pay table named "{editedName}" already exists. Do you want to overwrite it?</p>
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button onClick={handleOverwriteCancel} style={{
              padding: '8px 16px',
              backgroundColor: '#f0f0f0',
              border: '1px solid #ccc',
              borderRadius: '4px',
              cursor: 'pointer'
            }}>
              No
            </button>
            <button onClick={handleOverwriteConfirm} style={{
              padding: '8px 16px',
              backgroundColor: '#dc3545',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: '600'
            }}>
              Yes, Overwrite
            </button>
          </div>
        </div>
      )}
      {showRestoreConfirm && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          backgroundColor: 'white',
          padding: '24px',
          borderRadius: '8px',
          boxShadow: '0 8px 16px rgba(0, 0, 0, 0.3)',
          zIndex: 1001,
          minWidth: '300px'
        }}>
          <h3 style={{ marginTop: 0 }}>Restore Default?</h3>
          <p>This will reset "{activePaytable}" to its original default values. Any custom changes will be lost.</p>
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button onClick={handleRestoreCancel} style={{
              padding: '8px 16px',
              backgroundColor: '#f0f0f0',
              border: '1px solid #ccc',
              borderRadius: '4px',
              cursor: 'pointer'
            }}>
              Cancel
            </button>
            <button onClick={handleRestoreConfirm} style={{
              padding: '8px 16px',
              backgroundColor: '#ffc107',
              color: 'black',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: '600'
            }}>
              Yes, Restore Default
            </button>
          </div>
        </div>
      )}
      <div style={{
        backgroundColor: 'white',
        padding: '20px',
        borderRadius: '8px',
        maxWidth: '900px',
        maxHeight: '80vh',
        overflowY: 'auto',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
          <h2 style={{ margin: 0 }}>Keno Pay Table:</h2>
          {isEditMode ? (
            <input
              type="text"
              value={editedName}
              onChange={(e) => setEditedName(e.target.value)}
              style={{
                padding: '6px 10px',
                fontSize: '16px',
                fontWeight: 'bold',
                border: '2px solid #007bff',
                borderRadius: '4px',
                minWidth: '150px'
              }}
            />
          ) : (
            <span style={{ fontSize: '18px', fontWeight: 'bold', color: '#007bff' }}>
              {activePaytable}
            </span>
          )}
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{
            width: '100%',
            borderCollapse: 'collapse',
            marginBottom: '20px',
            fontSize: '12px'
          }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #ccc' }}>
                <th style={{ padding: '6px', textAlign: 'left', minWidth: '45px' }}>Hits</th>
                {spotCounts.map(spots => (
                  <th key={spots} style={{ padding: '6px', textAlign: 'center', minWidth: '55px', borderRight: '1px solid #eee' }}>
                    {spots} Spot{spots !== '1' ? 's' : ''}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(hitNum => (
                <tr key={hitNum} style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: '6px', fontWeight: 'bold' }}>{hitNum}</td>
                  {spotCounts.map(spots => (
                    <td key={`${spots}-${hitNum}`} style={{
                      padding: '6px',
                      textAlign: 'center',
                      borderRight: '1px solid #eee',
                      backgroundColor: currentPaytable[spots][hitNum] > 0 ? '#e7f3ff' : 'transparent'
                    }}>
                      {isEditMode ? (
                        <input
                          type="number"
                          min="0"
                          value={currentPaytable[spots][hitNum] || 0}
                          onChange={(e) => handleCellChange(spots, hitNum, e.target.value)}
                          style={{
                            width: '50px',
                            padding: '3px',
                            textAlign: 'center',
                            border: '1px solid #ccc',
                            borderRadius: '3px',
                            fontSize: '12px'
                          }}
                        />
                      ) : (
                        <span style={{ fontSize: '13px', fontWeight: 'bold', fontStyle: 'italic' }}>
                          {currentPaytable[spots][hitNum] || '-'}
                        </span>
                      )}
                    </td>
                  ))}
                </tr>
              ))}
              <tr style={{ borderTop: '2px solid #333', backgroundColor: '#f8f9fa' }}>
                <td style={{ padding: '8px', fontWeight: 'bold', fontSize: '13px' }}>Total RTP</td>
                {spotCounts.map(spots => (
                  <td key={`rtp-${spots}`} style={{
                    padding: '8px',
                    textAlign: 'center',
                    borderRight: '1px solid #eee',
                    fontWeight: 'bold',
                    fontSize: '13px',
                    color: '#007bff'
                  }}>
                    {calculateRTP(spots)}%
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px' }}>
          {!isEditMode ? (
            <>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button onClick={onPaytableClick} style={{
                  padding: '8px 16px',
                  backgroundColor: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: '600'
                }}>
                  Select Pay Table
                </button>
                <button onClick={() => isPremium && setIsEditMode(true)} disabled={!isPremium}
                  title={!isPremium ? 'Premium feature — upgrade to edit pay tables' : ''}
                  style={{
                  padding: '8px 16px',
                  backgroundColor: isPremium ? '#007bff' : '#888',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: isPremium ? 'pointer' : 'not-allowed',
                  fontWeight: '600'
                }}>
                  Edit Pay Table{!isPremium && ' ★'}
                </button>
                {defaultPaytables && defaultPaytables[activePaytable] && !isNewPaytable && (
                  <button onClick={handleRestoreDefault} style={{
                    padding: '8px 16px',
                    backgroundColor: '#ffc107',
                    color: 'black',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontWeight: '600'
                  }}>
                    Restore Default
                  </button>
                )}
              </div>
              <button onClick={onClose} style={{
                padding: '8px 16px',
                backgroundColor: '#f0f0f0',
                border: '1px solid #ccc',
                borderRadius: '4px',
                cursor: 'pointer'
              }}>
                Close
              </button>
            </>
          ) : (
            <>
              <button onClick={handleSave} style={{
                padding: '8px 16px',
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: '600'
              }}>
                Save Changes
              </button>
              <button onClick={handleCancel} style={{
                padding: '8px 16px',
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}>
                Cancel
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
